import { ProductGallery } from 'theme';

// --- Swatch system (isolated from gallery so failures don't cascade) ---

(function setupProductSwatches() {
  try {
    const CACHE_PREFIX = 'swatch_v1:';
    const CACHE_TTL = 60 * 60 * 1000; // 1 hour

    const memCache = new Map();
    const inFlight = new Map();
    const fetchQueue = [];
    let activeCount = 0;
    const CONCURRENCY = 3;
    let abortController = new AbortController();

    function readCache(handle) {
      if (memCache.has(handle)) {
        const entry = memCache.get(handle);
        if (Date.now() - entry.ts <= CACHE_TTL) return entry;
        memCache.delete(handle);
      }
      try {
        const raw = localStorage.getItem(CACHE_PREFIX + handle);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - data.ts > CACHE_TTL) {
          localStorage.removeItem(CACHE_PREFIX + handle);
          return null;
        }
        memCache.set(handle, data);
        return data;
      } catch {
        // Corrupt entry — remove it so it doesn't keep failing
        try { localStorage.removeItem(CACHE_PREFIX + handle); } catch {}
        return null;
      }
    }

    function writeCache(handle, data) {
      const entry = { ...data, ts: Date.now() };
      memCache.set(handle, entry);
      try {
        localStorage.setItem(CACHE_PREFIX + handle, JSON.stringify(entry));
      } catch {}
    }

    function processQueue() {
      while (activeCount < CONCURRENCY && fetchQueue.length) {
        const { handle, resolve, signal } = fetchQueue.shift();
        if (signal.aborted) { resolve(null); continue; }
        activeCount++;
        fetch(`/products/${handle}.js`, { signal })
          .then(async (res) => {
            if (res.status === 404) {
              // Definitive "doesn't exist" — safe to negative-cache
              writeCache(handle, { notFound: true });
              resolve(null);
              return;
            }
            if (!res.ok) {
              // Transient (429, 5xx) — don't cache, allow retry on next page load
              resolve(null);
              return;
            }
            const prod = await res.json();
            const colorOpt = (prod.options || []).find(
              o => typeof o.name === 'string' && o.name.toLowerCase().trim() === 'color'
            );
            const colors = colorOpt?.values ?? [];
            if (colors.length) {
              const data = { colors, url: prod.url, available: prod.available };
              writeCache(handle, data);
              resolve(data);
            } else {
              writeCache(handle, { notFound: true });
              resolve(null);
            }
          })
          .catch(() => resolve(null))
          .finally(() => { activeCount--; processQueue(); });
      }
    }

    function fetchProduct(handle) {
      const cached = readCache(handle);
      if (cached) return Promise.resolve(cached.notFound ? null : cached);
      if (inFlight.has(handle)) return inFlight.get(handle);

      const promise = new Promise((resolve) => {
        fetchQueue.push({ handle, resolve, signal: abortController.signal });
        processQueue();
      });
      inFlight.set(handle, promise);
      promise.finally(() => {
        if (inFlight.get(handle) === promise) inFlight.delete(handle);
      });
      return promise;
    }

    const handleValueCache = new Map();

    function buildHandleValue(value) {
      if (handleValueCache.has(value)) return handleValueCache.get(value);
      const result = value.toLowerCase()
        .split(' / ').join('-')
        .split('/').join('-')
        .split('&').join('-')
        .split(' ').join('-')
        .replace(/-+/g, '-');
      handleValueCache.set(value, result);
      return result;
    }

    function createSwatchItem(value, productData, handle, ownHandle) {
      const downcaseValue = value.toLowerCase();
      const colorValue = downcaseValue === 'brick' ? 'firebrick' : value;
      const handleValue = buildHandleValue(value);
      const imagePath = `//${window.location.host}/cdn/shop/files/${handleValue}.png?crop=center&height=64&width=64`;

      const li = document.createElement('li');
      li.className = 'HorizontalList__Item';

      const render = (hasImage) => {
        const a = document.createElement('a');
        a.className = [
          'product-color-swatch ColorSwatch color-swatch',
          downcaseValue === 'white' ? 'ColorSwatch--white' : '',
          handle === ownHandle ? 'selected is-selected' : '',
          !productData.available ? 'sold-out' : '',
          window.ifSwatchRound || '',
        ].join(' ').trim().replace(/\s+/g, ' ');
        a.href = productData.url;
        a.dataset.tooltip = value;
        a.style.setProperty(
          '--swatch-background',
          hasImage ? `url(${imagePath})` : colorValue.replace(/\s+/g, '').toLowerCase()
        );

        const span = document.createElement('span');
        span.className = 'u-visually-hidden sr-only';
        span.textContent = value;
        a.appendChild(span);

        li.replaceChildren(a);
      };

      const img = new Image();
      img.src = imagePath;
      img.onload = () => render(true);
      img.onerror = () => render(false);

      return li;
    }

    const loadSwatchesForCard = async (container) => {
      try {
        const colorGroupRaw = container.dataset.colorGroup;
        if (!colorGroupRaw) return;

        const colorGroup = colorGroupRaw.split('|').map(h => h.trim().toLowerCase()).filter(Boolean);
        if (!colorGroup.length) return;

        const ownHandle = container.dataset.ownColor;
        const ul = container.querySelector('.ColorSwatchList');
        if (!ul) return;

        const results = await Promise.all(colorGroup.map(handle => fetchProduct(handle)));

        // Card may have been removed from DOM by sort/filter during fetch
        if (!ul.isConnected) return;

        const swatchItems = [];
        results.forEach((data, i) => {
          if (!data) return;
          for (const value of data.colors) {
            swatchItems.push(createSwatchItem(value, data, colorGroup[i], ownHandle));
          }
        });

        if (swatchItems.length > 2 && ul.parentNode) {
          ul.innerHTML = '';
          ul.append(...swatchItems);

          const countLabel = (container.dataset.colorsAvailable || '__COUNT__ colors available').replace('__COUNT__', String(swatchItems.length));
          const countDiv = document.createElement('div');
          countDiv.textContent = countLabel;
          countDiv.className = 'smallcaps swatch-count-info';
          ul.parentNode.insertBefore(countDiv, ul);
        }
      } catch (err) {
        console.error('loadSwatchesForCard failed:', err);
      }
    };

    const swatchViewObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        swatchViewObserver.unobserve(entry.target);
        loadSwatchesForCard(entry.target);
      });
    }, { rootMargin: '400px' });

    const initProductSwatches = () => {
      document.querySelectorAll('.product-swatches--card:not([data-swatch-initialized])').forEach(card => {
        card.dataset.swatchInitialized = 'true';
        swatchViewObserver.observe(card);
      });
    };

    let debounceTimer = null;

    const mutationObserver = new MutationObserver((mutations) => {
      if (!mutations.some(m => m.addedNodes.length > 0)) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(initProductSwatches, 300);
    });

    document.addEventListener('facet:update', () => {
      try {
        abortController.abort();
        abortController = new AbortController();
        while (fetchQueue.length) fetchQueue.shift().resolve(null);
        inFlight.clear();
      } catch (err) {
        console.error('facet:update reset failed:', err);
      }
    });

    const boot = () => {
      initProductSwatches();
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    };

    // Handle the case where this script loads AFTER DOMContentLoaded has already fired
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  } catch (err) {
    console.error('Product swatch system failed to initialize:', err);
  }
})();

// --- Custom gallery arrows (isolated from swatch failures) ---

try {
  class CustomProductGallery extends ProductGallery {
    connectedCallback() {
      super.connectedCallback();

      if (this.hasAttribute('data-show-arrows')) {
        this.injectArrows();
      }
    }

    injectArrows() {
      if (this.querySelector('.custom-gallery-arrow')) return;

      const imageList = this.querySelector('.product-gallery__image-list');
      if (!imageList) return;

      imageList.style.position = 'relative';

      const prevBtn = document.createElement('button');
      prevBtn.className = 'custom-gallery-arrow custom-gallery-arrow--prev circle-button hover:animate-icon-inline';
      prevBtn.type = 'button';
      prevBtn.innerHTML = `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
          <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `;

      const nextBtn = document.createElement('button');
      nextBtn.className = 'custom-gallery-arrow custom-gallery-arrow--next circle-button hover:animate-icon-inline';
      nextBtn.type = 'button';
      nextBtn.innerHTML = `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
           <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
        </svg>
      `;

      imageList.appendChild(prevBtn);
      imageList.appendChild(nextBtn);

      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollPrevious();
      });

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollNext();
      });
    }

    getScroller() {
      return this.querySelector('scroll-carousel') || this.querySelector('[role="region"]');
    }

    scrollPrevious() {
      const scroller = this.getScroller();
      if (!scroller) return;

      if (scroller.scrollLeft <= 5) {
        scroller.scrollTo({ left: scroller.scrollWidth, behavior: 'smooth' });
      } else if (typeof scroller.prev === 'function') {
        scroller.prev();
      } else {
        const itemWidth = scroller.firstElementChild ? scroller.firstElementChild.clientWidth : 300;
        scroller.scrollBy({ left: -itemWidth, behavior: 'smooth' });
      }
    }

    scrollNext() {
      const scroller = this.getScroller();
      if (!scroller) return;

      if (scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 5) {
        scroller.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (typeof scroller.next === 'function') {
        scroller.next();
      } else {
        const itemWidth = scroller.firstElementChild ? scroller.firstElementChild.clientWidth : 300;
        scroller.scrollBy({ left: itemWidth, behavior: 'smooth' });
      }
    }
  }

  if (!window.customElements.get('custom-product-gallery')) {
    window.customElements.define('custom-product-gallery', CustomProductGallery);
  }
} catch (err) {
  console.error('CustomProductGallery init failed:', err);
}
